import type { Placement } from '@floating-ui/react'
import { useFloating, offset, shift, hide, flip } from '@floating-ui/react'
import type { ReactNode } from 'react'
import { cloneElement, isValidElement, useCallback, useEffect, useMemo } from 'react'

import type { IDropdownCourseContent } from './DropdownCourse'
import type { FnChild, IChildrenHandler } from './types'
import { getMergedHandlers } from './utils'

interface IUseEvents {
  inPopupOrChild: (element: Node) => boolean
  popupEle: HTMLDivElement | null
  mergedOpen: boolean
  triggerOpen: (nextOpen: boolean) => void
  type: IDropdownCourseContent['type']
}

interface IUseAlignParams {
  targetEle: HTMLElement | null
  popupEle: HTMLDivElement | null
  alignConfig: {
    x?: number
    y?: number
  }
  placement: IDropdownCourseContent['placement']
  triggerOpen: (open: boolean) => void
  fluid?: boolean
  open: boolean
  flip?: boolean
  shift?: boolean
}

interface IUseGetCloneElement {
  children?: IChildrenHandler | FnChild
  type: IDropdownCourseContent['type']
  triggerOpen: (nextOpen: boolean) => void
  stopPropagation?: boolean
  mergedOpen: boolean
}

const isBottom = (placement: Placement) => placement.toLowerCase().startsWith('bottom')
const isTop = (placement: Placement) => placement.toLowerCase().startsWith('top')
const isLeft = (placement: Placement) => placement.toLowerCase().startsWith('left')
const isRight = (placement: Placement) => placement.toLowerCase().startsWith('right')
const DEFAULT_SHIFT_OFFSET = 8
// const node: HTMLElement = ScrollService?.canvas?.ref || document.body

export const useAlign = ({
  targetEle,
  popupEle,
  alignConfig,
  placement = 'bottom',
  triggerOpen,
  fluid,
  open,
  flip: flipProp,
  shift: shiftProp,
}: IUseAlignParams) => {
  const { width } = targetEle?.getBoundingClientRect() || {}
  const isCrossAxis = isTop(placement) || isLeft(placement)
  const isMainAxis = isRight(placement) || isBottom(placement)

  const {
    x: left,
    y: top,
    update,
    middlewareData,
  } = useFloating({
    strategy: 'absolute',
    elements: { reference: targetEle, floating: popupEle },
    placement,
    open,
    onOpenChange: triggerOpen,
    middleware: [
      offset({
        mainAxis: alignConfig.y || (isMainAxis ? DEFAULT_SHIFT_OFFSET : 0),
        crossAxis: alignConfig.x || (isCrossAxis ? DEFAULT_SHIFT_OFFSET : 0),
      }),
      ...(flipProp ? [flip()] : []),
      ...(shiftProp ? [shift({ padding: DEFAULT_SHIFT_OFFSET })] : []),
      hide(),
    ],
  })

  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController
    update()
    const resizeObserver = new ResizeObserver(() => {
      update()
    })

    if (targetEle) {
      resizeObserver.observe(targetEle)
    }
    if (middlewareData.hide?.referenceHidden) {
      triggerOpen(false)
    }

    window.addEventListener('resize', update, { signal })
    window.addEventListener('scroll', update, { capture: true, signal })

    return () => {
      abortController.abort()
      resizeObserver.disconnect()
    }
  }, [targetEle, triggerOpen, update, middlewareData.hide?.referenceHidden])

  return useMemo(
    () => ({
      top,
      left,
      ...(fluid && { width }),
    }),
    [fluid, left, top, width],
  )
}

export const useEvents = ({
  inPopupOrChild,
  triggerOpen,
  mergedOpen,
  popupEle,
  type,
}: IUseEvents) => {
  useEffect(() => {
    if (popupEle) {
      const abortController = new AbortController()
      const signal = abortController.signal

      const onWindowClick = (e: MouseEvent) => {
        const target = e.target as Node
        if (mergedOpen && !inPopupOrChild(target)) {
          triggerOpen(false)
        }
      }

      const closeOnEsc = (e: KeyboardEvent) => {
        const target = e.target as Node
        if (e.key === 'Escape' && mergedOpen && !inPopupOrChild(target)) {
          triggerOpen(false)
        }
      }

      const onOutsideLeave = (e: MouseEvent) => {
        const target = e.target as Node
        if (type === 'tooltip' && mergedOpen && !inPopupOrChild(target)) {
          triggerOpen(false)
        }
      }
      document.addEventListener('mouseover', onOutsideLeave, { signal })
      document.addEventListener('mousedown', onWindowClick, { signal, capture: true })
      document.addEventListener('keydown', closeOnEsc, { signal })

      return () => {
        abortController.abort()
      }
    }
    return () => undefined
  }, [inPopupOrChild, mergedOpen, popupEle, triggerOpen, type])
}

export const useGetCloneElement = ({
  children,
  type,
  triggerOpen,
  mergedOpen,
}: IUseGetCloneElement) => {
  const getClonedChild = useCallback(
    (children?: IChildrenHandler | FnChild | ReactNode) =>
      isValidElement(children)
        ? cloneElement(children, {
            ...getMergedHandlers({ children: children as IChildrenHandler, type, triggerOpen }),
            ...(type === 'dropdown' && {
              'aria-haspopup': 'true',
              'aria-expanded': mergedOpen,
              role: 'button',
            }),
          })
        : children,
    [type, triggerOpen, mergedOpen],
  )

  const childNode = useMemo(() => {
    const clonedChild = getClonedChild(children)
    if (typeof clonedChild === 'function') {
      const node = clonedChild({ open: mergedOpen, setOpen: triggerOpen })
      const coneNode = getClonedChild(node)
      if (coneNode) {
        return coneNode
      }
      return node
    }
    return clonedChild
  }, [getClonedChild, children, mergedOpen, triggerOpen])

  return childNode
}
